//VisualBriefSlideShow.js
import React, { useCallback, useState, useEffect, useRef  } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlay, faPlayCircle, faStop, faVolumeMute, faVolumeUp, faSpinner, faComment, faVideo, 
  faVideoSlash, faShare, faGlobe, faVideoCamera, faPhotoVideo, faHeart, faThumbsUp, faPaperPlane,
  faUsers,
  faUser,
  faChalkboardUser
 } from '@fortawesome/free-solid-svg-icons';
import { faYoutube, faFacebook, faGoogle } from '@fortawesome/free-brands-svg-icons';

import Speech from 'speak-tts';
import './VisualBriefSlideShow.css';

import vbAd from '../../assets/pageimages/vbAd.webp';

const VisualBriefSlideShow = () => {
  const authState = useSelector(state => state.authReducer);
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.thin5whyscode);

  const navigate = useNavigate();

  const [fetchData, { loading }] = useFetch();

  const location = useLocation();

  // console.log("location",location);

  const vbMasterId = location.state.vbMasterId;
  const vbMasterTitle = location.state.title;
  const noOfSlides = location.state.noOfSlides;
  const isPresenting = location.state.isPresenting;
  
  const slideNumber = 1;

  const [slideCount, setSlideCount] = useState(noOfSlides);
  const [slideSet, setSlideSet] = useState([]);
  const [slideImage, setSlideImage] = useState('');
  const [slideTitle, setSlideTitle] = useState('');
  const [slideDesc, setSlideDesc] = useState('');
  const [webSites, setWebsites] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [tedUrl, setTedUrl] = useState('');
  const [vimeoUrl, setVimeoUrl] = useState('');
  const [currentSlideNumber, setCurrentSlideNumber] = useState(slideNumber);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [pageLoadTime, setPageLoadTime] = useState(null);
  const [isInteracted, setIsInteracted] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPageLoadTime(new Date());
  }, []);

  // Save Access Log
  const saveAccessLog = async () => {
    const localDateString = new Date(pageLoadTime - pageLoadTime.getTimezoneOffset() * 60000).toISOString();
    try {
      const response = await fetch("/api/vbaccessors", {
        method: "post",
        headers: {
          Authorization: authState.token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          vbMasterId: vbMasterId,
          userId: userId,
          dateAccessed: localDateString,
          userCode: userCode,
          userName: userName,
          slideTitle,
          isActive: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save access log");
      }

      const data = await response.json();
      setSuccessMessage(true);
      setTimeInMiliseconds(0);
      setPageLoadTime(new Date());
    } catch (error) {
      console.error("Error while saving read log:", error);
    }
  };


  // Slide
  const fetchSlide = useCallback(() => {
    const config = {
      url: "/vbslideview",
      method: "post",
      headers: { Authorization: authState.token },
      data: {
        vbMasterId,
        currentSlideNumber,
      }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        // console.log("Response:", response);
        if (response) {
          setSlideSet(response);
          setError(null);
        } else {
          console.error("Invalid response format: Slide List not found in response");
        }
      })
      .catch(error => {
        console.error("Error fetching slide details:", error);
      });
  }, [authState.token, fetchData, vbMasterId, currentSlideNumber, setSlideSet]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchSlide();
  }, [authState.isLoggedIn, fetchSlide, location.state.vbMasterId, location.state.vbTitle, location.state.noOfSlides]);

  // const handleNextSlide = () => {
  //   if (currentSlideNumber < slideCount) {
  //     setCurrentSlideNumber(currentSlideNumber + 1);
  //   }
  // };

  // const handlePrevSlide = () => {
  //   if (currentSlideNumber > 1) {
  //     setCurrentSlideNumber(currentSlideNumber - 1);
  //   }
  // };

  const handleNextSlide = () => {
    if (currentSlideNumber < slideCount) {
      speech.cancel();
      setIsSpeaking(false);
      setCurrentSlideNumber(currentSlideNumber + 1);
    }
    saveAccessLog();
  };
  
  const handlePrevSlide = () => {
    if (currentSlideNumber > 1) {
      speech.cancel();
      setIsSpeaking(false);
      setCurrentSlideNumber(currentSlideNumber - 1);
    }
    saveAccessLog();
  };

  const handleSwipe = () => {
    setIsInteracted(true);
    saveAccessLog();
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      handleSwipe();
      handleNextSlide();
    },
    onSwipedRight: () => {
      handleSwipe();
      handlePrevSlide();
    },
  });

  const handleClose = () => {
    // window.location.href = '/visualbrief-library';
    saveAccessLog();
    navigate(-1);
  };

  const speech = new Speech();
  const toggleSpeech = () => {
    if (isSpeaking) {
      speech.cancel();
      setIsSpeaking(false);
    } else {
      setIsSpeaking(true);
      speech.setRate(0.8); // Set speech rate to 1.5 times the normal speed
      speech.setPitch(1.2); // Set pitch to 1.2 (higher pitch)
      speech.setVolume(1.0); // Set volume to 100%
      speech.setLanguage('en-US'); // Set language 
      const textToSpeak = slideSet.onScreenText ? slideSet.slideDesc : slideSet.ttsText;
      speech.speak({ text: textToSpeak });
    }
  };

  const comingSoon = () => {
    //
  };

  useEffect(() => {
    return () => {
      if (isSpeaking) {
        speech.cancel();
        setIsSpeaking(false);
      }
    };
  }, [isSpeaking]);

  
  const accessWebsite = (url) => {
    if (!url) {
        console.error("Website URL not provided.");
        return;
    }
    const formattedURL = url.startsWith("http") ? url : `https://${url}`;
    // console.log("Opening Website URL:", formattedURL);
    window.open(formattedURL, "_blank");
  };  

  const watchYouTube = (url) => {
    if (!url) {
        console.error("YouTube URL not provided.");
        return;
    }

    const formattedURL = url.startsWith("http") ? url : `https://${url}`;
    // console.log("Opening YouTube URL:", formattedURL);
    window.open(formattedURL, "_blank");
  };  

  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);

  const handleVideoToggle = () => {
    if (slideSet.think5whysVideoURL) {
      setShowVideo(!showVideo);
    } else {
      console.error("Think5Whys Cloud Video URL not provided.");
    }
  };

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [showComments, setShowComments] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);

  // // Custom fetch hook
  // const useFetch = async (url, options = {}) => {
  //   const response = await fetch(url, options);
  //   return response.json();
  // };

  // Toggle comment section
  const toggleComments = (slideid) => {
    setShowComments(!showComments);
    setCommentSuccess(false);
    setNewComment("");
    if (!showComments) {
      fetchComments(slideid);
    }
  };

  const fetchComments = async (slideid) => {
    const config = {
      url: "/vbslidecomments/vbslideid",
      method: "post",
      headers: { Authorization: authState.token },
      data: { slideId: slideid }
    };
  
    try {
      const data = await fetchData(config, { showSuccessToast: false });
      
      if (data?.vbslideComments) {
        setComments(data.vbslideComments);
      } else {
        console.error("Error fetching comments:", data?.message || "Unknown error");
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };
  
  // Handle comment submission
  const submitComment = async (slideid, title) => {
    if (!newComment.trim()) {
      alert("Comment cannot be blank.");
      return;
    }

    if (newComment.length > 2500) {
      alert("Comment must be within 2500 characters.");
      return;
    }

  try {
    const comment = {
      vbSlideId: slideid,
      userId: userId, 
      userComment: newComment,
      vbSlideTitle: title, 
      userName: userName, 
      commentVisibility: "public", 
      vbslideCommentRCACode: "", 
      dateCommented: new Date(),
    };

    const config = {
      url: "/vbslidecomments",
      method: "post",
      headers: { Authorization: authState.token },
      data: { comment }
    };

    fetchData(config).then(() => {
      // console.log("Success");
      setCommentSuccess(true);
    });
  } catch (error) {
    console.error("Error submitting comment:", error);
  }
};

const [users, setUsers] = useState([]);
const [newUserId, setNewUserId] = useState(null);
const [newUserCode, setNewUserCode] = useState("");
const [newUserName, setNewUserName] = useState("");
const [remarks, setRemarks] = useState("");
const [showUsers, setShowUsers] = useState(false);
const [userSuccess, setUserSuccess] = useState(false);

// Toggle Users section
const toggleUsers = () => {
  setShowUsers(!showUsers);
  setUserSuccess(false);
  setNewUserId(null);
  setNewUserCode("");
  setNewUserName("");
  setRemarks("");
  if (!showUsers) {
    fetchUsers();
  }
};

const fetchUsers = async () => {
  const config = {
    url: "/vbsharedusers/vbmasterid",
    method: "post",
    headers: { Authorization: authState.token },
    data: { vbMasterId: vbMasterId, ownerId: userId }
  };

  try {
    const data = await fetchData(config, { showSuccessToast: false });
    
    if (data?.vbsharedUsers) {
      setUsers(data.vbsharedUsers);
    } else {
      console.error("Error fetching users:", data?.message || "Unknown error");
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

// Handle user submission
const submitUser = async () => {
if (!newUserCode.trim()) {
  alert("user cannot be blank.");
  return;
}

if (newUserCode.length != 14) {
  alert("User Code must be 14 characters.");
  return;
}

try {
  const user = {
    vbMasterId: vbMasterId,
    ownerId: userId, 
    ownerCode: userCode,
    ownerName: userName,
    vbMasterTitle: vbMasterTitle, 
    userName: newUserName, 
    userCode: newUserCode,
    userId: newUserId,
    remarks: remarks,
    isActive: true,
    dateCreated: new Date(),
  };

  const config = {
    url: "/vbsharedusers",
    method: "post",
    headers: { Authorization: authState.token },
    data: { user }
  };

  fetchData(config).then(() => {
    // console.log("Success");
    setUserSuccess(true);
  });
} catch (error) {
  console.error("Error saving user:", error);
}
};

// Groups
const [groups, setGroups] = useState([]);
const [newGroupId, setNewGroupId] = useState(null);
const [newGroupCode, setNewGroupCode] = useState("");
const [newGroupName, setNewGroupName] = useState("");
const [groupRemarks, setGroupRemarks] = useState("");
const [showGroups, setShowGroups] = useState(false);
const [groupSuccess, setGroupSuccess] = useState(false);

const toggleGroups = () => {
  setShowGroups(!showGroups);
  setGroupSuccess(false);
  setNewGroupId(null);
  setNewGroupCode("");
  setNewGroupName("");
  setRemarks("");
  if (!showGroups) {
    fetchGroups();
  }
};

const fetchGroups = async () => {
  const config = {
    url: "/vbsharedgroups/vbmasterid",
    method: "post",
    headers: { Authorization: authState.token },
    data: { vbMasterId: vbMasterId, ownerId: userId }
  };

  try {
    const data = await fetchData(config, { showSuccessToast: false });
    
    if (data?.vbsharedGroups) {
      setGroups(data.vbsharedGroups);
    } else {
      console.error("Error fetching groups:", data?.message || "Unknown error");
    }
  } catch (error) {
    console.error("Error fetching groups:", error);
  }
};

// Handle user submission
const submitGroup = async () => {
if (!newGroupCode.trim()) {
  alert("Group Code cannot be blank.");
  return;
}

if (newGroupCode.length != 14) {
  alert("Group Code must be 14 characters.");
  return;
}

try {
  const group = {
    vbMasterId: vbMasterId,
    ownerId: userId, 
    ownerCode: userCode,
    ownerName: userName,
    vbMasterTitle: vbMasterTitle, 
    groupName: newGroupName, 
    groupCode: newGroupCode,
    groupId: newGroupId,
    remarks: groupRemarks,
    isActive: true,
    dateCreated: new Date(),
  };

  const config = {
    url: "/vbsharedgroups",
    method: "post",
    headers: { Authorization: authState.token },
    data: { group }
  };

  fetchData(config).then(() => {
    // console.log("Success");
    setGroupSuccess(true);
  });
} catch (error) {
  console.error("Error saving user:", error);
}
};

// Presentation Remarks
const [presented, setPresented] = useState([]);
const [presentedRemarks, setPresentedRemarks] = useState("");
const [showPresented, setShowPresented] = useState(false);
const [presentedSuccess, setPresentedSuccess] = useState(false);

const togglePresented = () => {
  setShowPresented(!showPresented);
  setPresentedSuccess(false);
  setPresentedRemarks("");
  if (!showPresented) {
    fetchPresented();
  }
};

const fetchPresented = async () => {
  const config = {
    url: "/vbpresented/vbmasterid",
    method: "post",
    headers: { Authorization: authState.token },
    data: { vbMasterId: vbMasterId, presenterId: userId }
  };

  try {
    const data = await fetchData(config, { showSuccessToast: false });
    
    if (data?.vbpresented) {
      setPresented(data.vbpresented);
    } else {
      console.error("Error fetching Presented Remarks:", data?.message || "Unknown error");
    }
  } catch (error) {
    console.error("Error fetching Presented Remarks:", error);
  }
};

const submitPresented = async (slidenumber, slidetitle) => {
if (!presentedRemarks.trim()) {
  alert("Remarks cannot be blank.");
  return;
}

if (presentedRemarks.length > 500) {
  alert("Remarks can not exceeds 500 characters.");
  return;
}

try {
  const presented = {
    vbMasterId: vbMasterId,
    presenterId: userId, 
    presenterCode: userCode,
    presenterName: userName,
    presenteeId: userId, 
    presenteeCode: userCode,
    presenteeName: userName,
    vbMasterTitle: vbMasterTitle, 
    vbSlideNumber: slidenumber,
    vbSlideTitle: slidetitle,
    remarks: presentedRemarks,
    isActive: true,
    dateCreated: new Date(),
  };

  const config = {
    url: "/vbpresented",
    method: "post",
    headers: { Authorization: authState.token },
    data: { presented }
  };

  fetchData(config).then(() => {
    // console.log("Success");
    setPresentedSuccess(true);
  });
} catch (error) {
  console.error("Error saving Presentation Remarks:", error);
}
};

  return (
    <div className="my-2 mx-auto max-w-[750px] py-4 bg-white text-black fixed-height-container" style={{ height: '100vh', overflow: 'auto' }} {...swipeHandlers}>
      {loading ? (
        <div className="spinner-container">
          <div className="spinner">
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        </div>
      ) : (
        <>
          <div className="fixed-height-container textAlign: 'center'" style={{ flex: 1, overflowY: 'auto' }}>
            <div className="flex justify-between items-center mt-2 mb-2 bg-cyan-900 text-white">
              <div style={{ width: '90%', maxHeight: '40px', overflow: 'hidden' }}>
                <h3 className="text-lg mt-1 mb-4 mx-2 max-w-[750px] bg-cyan-900 text-white">
                  <i className="fa-solid fa-sliders" aria-hidden="true"></i>   {vbMasterTitle}
                </h3>
              </div>
              <div style={{ width: '10%', maxHeight: '40px', overflow: 'hidden' }}>
                <button className="mx-4  bg-cyan-900" onClick={handleClose}>
                  <i className="fa fa-times" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <div class="ml-2 flex justify-center textAlign: 'center'">
              <button className="mr-2" onClick={toggleSpeech} disabled={!slideSet.hasTTS}>
                {isSpeaking ? <FontAwesomeIcon icon={faStop} /> : <FontAwesomeIcon icon={faPlay} />}
              </button>
              <button className="mr-2" onClick={() => toggleComments(slideSet._id)}>
                <FontAwesomeIcon icon={faComment} /> 
              </button>
              <button className="mr-2" onClick={() => accessWebsite(slideSet.websites)} disabled={slideSet.websites=== ''}>
                <FontAwesomeIcon icon={faGlobe} /> 
              </button>
              <button className="mr-2" onClick={() => watchYouTube(slideSet.youtubeURL)} disabled={slideSet.youtubeURL=== ''}>
                <FontAwesomeIcon icon={faYoutube} className='text-red-500'/> 
              </button>
              <button className="mr-2" onClick={handleVideoToggle} disabled={slideSet.tedURL=== ''}>
                <FontAwesomeIcon icon={faVideoSlash} /> 
              </button>
              {slideSet.createdBy === userId && (
                <>
                <button className="mr-2" onClick={() => toggleUsers()}>
                  <FontAwesomeIcon icon={faUser} /> 
                </button>
                <button className="mr-2" onClick={() => toggleGroups()}>
                  <FontAwesomeIcon icon={faUsers} /> 
                </button>
                </>
              )}
              {isPresenting && (
                <button className="mr-2" onClick={togglePresented}>
                  <FontAwesomeIcon icon={faChalkboardUser} /> 
                </button>
              )}
            </div>
            {/* <div className='ml-2 mr-2 page-content flex align-middle justify-center' >
                <img src={vbAd} />
            </div> */}
            <div className='mt-2 mb-2  bg-cyan-800 text-white align-middle' style={{ textAlign: 'center' }}>
              {slideSet.title}
            </div>
            {showComments && (
              <div className="fixed inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-5 rounded-lg shadow-lg w-11/12 md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-y-auto relative">
                  <div className='flex flex-row justify-between'>
                    <h3 className="text-lg text-blue-800 font-bold mb-2 pt-4">Comments</h3>
                    <button
                      className="text-yellow-200 hover:text-yellow-300 text-xl w-10"
                      onClick={() => setShowComments(false)}
                    >
                      ✖
                    </button>
                  </div>
                  {comments.length > 0 ? (
                    comments.map((comment) => (
                      <div key={comment._id} className="mb-3 p-2 border-b bg-gray-600 text-white">
                        <p className="text-sm font-semibold">
                          {comment.userName} - {new Date(comment.dateCommented).toLocaleString()}
                        </p>
                        <p className="text-white">{comment.userComment}</p>
                        <div className="flex space-x-2 mt-1">
                          {comment.commentIsLoved && <FontAwesomeIcon icon={faHeart} className="text-red-500" />}
                          {comment.commentIsLiked && <FontAwesomeIcon icon={faThumbsUp} className="text-blue-500" />}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No comments yet.</p>
                  )}

                  {/* Input for new comment */}
                  <textarea
                    className="w-full border p-2 mt-3 bg-gray-100 text-black rounded"
                    placeholder="Write your comment..."
                    value={newComment}
                    disabled={commentSuccess}
                    onChange={(e) => setNewComment(e.target.value)}
                    maxLength={2500}
                    style={{width: "100%", height:"100px"}}
                  ></textarea>

                  <button
                    className="bg-blue-500 text-white px-4 py-2 mt-2 rounded"
                    disabled={commentSuccess}
                    onClick={() => submitComment(slideSet._id, slideSet.title)}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="mr-2" /> Submit
                  </button>
                  {commentSuccess && (
                    <div className='flex justify-center'>
                      <p className='text-green-700'>Comment Posted Successfully!!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showUsers && (
              <div className="fixed inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-5 rounded-lg shadow-lg w-11/12 md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-y-auto relative">
                  <div className='flex flex-row justify-between'>
                    <h3 className="text-lg text-blue-800 font-bold mb-2 pt-4">Users Who Can Access This Presentation</h3>
                    <button
                      className="text-yellow-200 hover:text-yellow-300 text-xl w-10"
                      onClick={() => setShowUsers(false)}
                    >
                      ✖
                    </button>
                  </div>
                  {users.length > 0 ? (
                    users.map((user) => (
                      <div key={user._id} className="mb-3 p-2 border-b bg-gray-600 text-white">
                        <p className="text-sm text-yellow-200">User Name: {user.userName}</p>
                        <p className="text-white text-sm">User Code: {user.userCode}</p>
                        <p className="text-white text-sm">Is Active: {user.isActive? "Yes": "No"}</p>
                        <p className="text-sm font-semibold">
                          Since: {new Date(user.dateCreated).toLocaleString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No users allowed yet.</p>
                  )}
                  <div className='flex flex-col text-black'>
                    <p className='text-black'>14 Character Think5Whys User Code:</p>
                    <input
                      className="w-full border p-2 mt-3 bg-gray-100 text-black rounded"
                      placeholder="Input User Code.."
                      value={newUserCode}
                      disabled={userSuccess}
                      onChange={(e) => setNewUserCode(e.target.value)}
                      style={{width: "200px"}}
                    ></input>
                    <p className='text-black'>User Name:</p>
                    <input
                      className="w-full border p-2 mt-3 bg-gray-100 text-black rounded"
                      placeholder="Input User Name.."
                      value={newUserName}
                      disabled={userSuccess}
                      onChange={(e) => setNewUserName(e.target.value)}
                      style={{width: "350px"}}
                    ></input>
                    <p className='text-black'>Remarks:</p>
                    <textarea
                      className="w-full border p-2 mt-3 bg-gray-100 text-black rounded"
                      placeholder="Write your remarks..."
                      value={remarks}
                      disabled={userSuccess}
                      onChange={(e) => setRemarks(e.target.value)}
                      maxLength={2500}
                      style={{width: "100%", height:"100px"}}
                    ></textarea>
                  </div>
                  <button
                    className="bg-blue-500 text-white px-4 py-2 mt-2 rounded"
                    disabled={userSuccess}
                    onClick={() => submitUser()}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="mr-2" /> Save
                  </button>
                  {userSuccess && (
                    <div className='flex justify-center'>
                      <p className='text-green-700'>User Allowed Access Successfully!!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showGroups && (
              <div className="fixed inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-5 rounded-lg shadow-lg w-11/12 md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-y-auto relative">
                  <div className='flex flex-row justify-between'>
                    <h3 className="text-lg text-blue-800 font-bold mb-2 pt-4">Groups Who Can Access This Presentation</h3>
                    <button
                      className="text-yellow-200 hover:text-yellow-300 text-xl w-10"
                      onClick={() => setShowGroups(false)}
                    >
                      ✖
                    </button>
                  </div>
                  {groups.length > 0 ? (
                    groups.map((group) => (
                      <div key={group._id} className="mb-3 p-2 border-b bg-gray-600 text-white">
                        <p className="text-sm text-yellow-200">Group Name: {group.groupName}</p>
                        <p className="text-white text-sm">Group Code: {group.groupCode}</p>
                        <p className="text-white text-sm">Is Active: {group.isActive? "Yes": "No"}</p>
                        <p className="text-sm font-semibold">
                          Since: {new Date(group.dateCreated).toLocaleString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No groups allowed yet.</p>
                  )}
                  <div className='flex flex-col text-black'>
                    <p className='text-black'>14 Character Think5Whys Group Code:</p>
                    <input
                      className="w-full border p-2 mt-3 bg-gray-100 text-black rounded"
                      placeholder="Input Group Code.."
                      value={newGroupCode}
                      disabled={groupSuccess}
                      onChange={(e) => setNewGroupCode(e.target.value)}
                      style={{width: "200px"}}
                    ></input>
                    <p className='text-black'>Group Name:</p>
                    <input
                      className="w-full border p-2 mt-3 bg-gray-100 text-black rounded"
                      placeholder="Input Group Name.."
                      value={newGroupName}
                      disabled={groupSuccess}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      style={{width: "350px"}}
                    ></input>
                    <p className='text-black'>Remarks:</p>
                    <textarea
                      className="w-full border p-2 mt-3 bg-gray-100 text-black rounded"
                      placeholder="Write your remarks..."
                      value={groupRemarks}
                      disabled={groupSuccess}
                      onChange={(e) => setGroupRemarks(e.target.value)}
                      maxLength={2500}
                      style={{width: "100%", height:"100px"}}
                    ></textarea>
                  </div>
                  <button
                    className="bg-blue-500 text-white px-4 py-2 mt-2 rounded"
                    disabled={groupSuccess}
                    onClick={() => submitGroup()}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="mr-2" /> Save
                  </button>
                  {groupSuccess && (
                    <div className='flex justify-center'>
                      <p className='text-green-700'>Group Allowed Access Successfully!!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showPresented && (
              <div className="fixed inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-5 rounded-lg shadow-lg w-11/12 md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-y-auto relative">
                  <div className='flex flex-row justify-between'>
                    <h3 className="text-lg text-blue-800 font-bold mb-2 pt-4">Presentation Remarks</h3>
                    <button
                      className="text-yellow-200 hover:text-yellow-300 text-xl w-10"
                      onClick={() => setShowPresented(false)}
                    >
                      ✖
                    </button>
                  </div>
                  {presented.length > 0 ? (
                    presented.map((present) => (
                      <div key={present._id} className="mb-3 p-2 border-b bg-gray-600 text-white">
                        <p className="text-white text-sm">{present.remarks}</p>
                        <p className="text-sm text-yellow-200">Remarks By: {present.presenteeName}</p>
                        <p className="text-sm font-semibold">
                          Date: {new Date(present.dateCreated).toLocaleString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No presentation remarks yet!</p>
                  )}
                  <div className='flex flex-col text-black'>
                    <p className='text-black'>Remarks:</p>
                    <textarea
                      className="w-full border p-2 mt-3 bg-gray-100 text-black rounded"
                      placeholder="Write your remarks..."
                      value={presentedRemarks}
                      disabled={presentedSuccess}
                      onChange={(e) => setPresentedRemarks(e.target.value)}
                      maxLength={500}
                      style={{width: "100%", height:"100px"}}
                    ></textarea>
                  </div>
                  <button
                    className="bg-blue-500 text-white px-4 py-2 mt-2 rounded"
                    disabled={presentedSuccess}
                    onClick={() => submitPresented()}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="mr-2" /> Save
                  </button>
                  {presentedSuccess && (
                    <div className='flex justify-center'>
                      <p className='text-green-700'>Remarks Posted Successfully!!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showVideo && (
              <video
                ref={videoRef}
                controls
                width="100%"
                src={`/api/downloads/visualbrief/slidevideo/${slideSet.think5whysVideoURL}.mp4`}
                style={{ display: "block", marginTop: "10px" }}
              />
            )}
            <div className="mt-2 flex justify-center">
              <img 
                  src={
                      `/api/downloads/visualbrief/${slideSet.slideImageData}.png` 
                  }
                  alt={"No Image " + slideSet.title} 
                  className="rounded-md flex justify-items-center"
              />
            </div>
            <div className='mt-4 page-content flex' style={{ Width: '100%', Height: '100%' }}
              dangerouslySetInnerHTML={{ __html: slideSet.slideDesc }} />
            
            {slideSet.hasAddlDesc && (
              <div className='mt-2 mb-2 page-content flex bg-yellow-200 justify-between' style={{ Width: '100%', Height: '100%' }}
                dangerouslySetInnerHTML={{ __html: slideSet.addlDesc }} />
            )}
            <div className="page-navigation bg-cyan-900 text-white px-2 py-2" style={{ textAlign: 'center', display: 'flex', justifyContent: 'space-around' }}>
              <div style={{ width: '20%' }}>
                <button title="Prev" className="tooltip-button" onClick={handlePrevSlide} disabled={currentSlideNumber === 1}>
                  <i className="fa fa-angle-left" aria-hidden="true"></i>
                </button>
              </div>
              <div style={{ width: '40%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span>Slide#</span>
                <select
                  className='bg-black border-solid border-2 border-cyan-900 ml-4'
                  value={currentSlideNumber}
                  onChange={(e) => setCurrentSlideNumber(parseInt(e.target.value))}
                >
                  {[...Array(slideCount).keys()].map(slide => (
                    <option key={slide + 1} value={slide + 1}>{slide + 1}</option>
                  ))}
                </select>
              </div>
              <div style={{ width: '20%' }}>
                <button title="Next" className="tooltip-button" onClick={handleNextSlide} disabled={currentSlideNumber === slideCount}>
                  <i className="fa fa-angle-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VisualBriefSlideShow;
